import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { VoiceCreationService } from "@/services/integrations/elevenlabs/VoiceCreationService";
import {
  PvcCreateRequest,
  PvcCreateResponse,
  PvcSample,
  PvcSampleUpdateRequest,
  PvcSampleUploadRequest,
  PvcSpeakerStatus,
  PvcTrainRequest,
  PvcStatusResponse,
} from "@/types/voice/voice-creation";

const voiceCreationService = VoiceCreationService.getInstance();

export const useProfessionalVoiceClone = () => {
  const create = useMutation<PvcCreateResponse, Error, PvcCreateRequest>({
    mutationFn: (payload) => voiceCreationService.createPvc(payload),
    onError: (error) => {
      console.error("PVC create failed:", error);
      toast.error("Failed to create professional voice clone.");
    },
  });

  const uploadSamples = useMutation<PvcSample[], Error, PvcSampleUploadRequest>({
    mutationFn: (payload) => voiceCreationService.uploadPvcSamples(payload),
    onError: (error) => {
      console.error("PVC sample upload failed:", error);
      toast.error("Failed to upload PVC samples.");
    },
  });

  const separateSpeakers = useMutation({
    mutationFn: ({ voiceId, sampleId }: { voiceId: string; sampleId: string }) =>
      voiceCreationService.separatePvcSpeakers(voiceId, sampleId),
    onError: (error: Error) => {
      console.error("PVC speaker separation failed:", error);
      toast.error("Failed to start speaker separation.");
    },
  });

  const speakerStatus = useMutation<PvcSpeakerStatus, Error, { voiceId: string; sampleId: string }>({
    mutationFn: ({ voiceId, sampleId }) => voiceCreationService.getPvcSpeakerStatus(voiceId, sampleId),
    onError: (error) => {
      console.error("PVC speaker status failed:", error);
      toast.error("Failed to fetch speaker status.");
    },
  });

  const updateSample = useMutation({
    mutationFn: (payload: PvcSampleUpdateRequest) => voiceCreationService.updatePvcSample(payload),
    onError: (error: Error) => {
      console.error("PVC sample update failed:", error);
      toast.error("Failed to update PVC sample.");
    },
  });

  const getCaptcha = useMutation({
    mutationFn: (voiceId: string) => voiceCreationService.getPvcCaptcha(voiceId),
    onError: (error: Error) => {
      console.error("PVC captcha request failed:", error);
      toast.error("Failed to fetch verification captcha.");
    },
  });

  const verifyCaptcha = useMutation({
    mutationFn: ({ voiceId, recording }: { voiceId: string; recording: File }) =>
      voiceCreationService.verifyPvcCaptcha(voiceId, recording),
    onError: (error: Error) => {
      console.error("PVC captcha verification failed:", error);
      toast.error("Failed to verify captcha recording.");
    },
  });

  const requestManualVerification = useMutation({
    mutationFn: ({ voiceId, files, extraText }: { voiceId: string; files: File[]; extraText?: string }) =>
      voiceCreationService.requestPvcManualVerification(voiceId, files, extraText),
    onError: (error: Error) => {
      console.error("PVC manual verification failed:", error);
      toast.error("Failed to request manual verification.");
    },
  });

  const train = useMutation({
    mutationFn: (payload: PvcTrainRequest) => voiceCreationService.trainPvc(payload),
    onError: (error: Error) => {
      console.error("PVC training failed:", error);
      toast.error("Failed to start PVC training.");
    },
  });

  const status = useMutation<PvcStatusResponse, Error, string>({
    mutationFn: (voiceId) => voiceCreationService.getPvcStatus(voiceId),
    onError: (error) => {
      console.error("PVC status failed:", error);
      toast.error("Failed to fetch PVC status.");
    },
  });

  return {
    create,
    uploadSamples,
    separateSpeakers,
    speakerStatus,
    updateSample,
    getCaptcha,
    verifyCaptcha,
    requestManualVerification,
    train,
    status,
  };
};
